import React from 'react';
import {initialYandexZoomValue, ViewProps} from '../models';
import {Text} from '../../../../../../shared/components/text';
import {View} from 'react-native';
import {useStyles} from '../../../../../../shared/hooks/styles';
import {useTheme} from '../../../../../../shared/theme';
import {images} from '../../../../../../assets/icons';
import LoadableComponent from '../../../../../../shared/components/loadableContent/view';
import {Clusterer, Map, Placemark, YMaps} from 'react-yandex-maps';
import MapMarker from '../../../../../../assets/icons/map-marker.svg';
import MapPin from '../../../../../../assets/icons/map-pin.svg';
import {DeviceSize} from '../../../../../../shared/theme/helpers';
import {formatPhone, formatPhoneToCallable} from '../../../../../../shared/pipes';
import {Link} from '../../../../../../shared/components/link';
import {MenuItem, TextField, withStyles} from '@material-ui/core';
import {Map as MapType} from 'yandex-maps';

export const MobileView = ({
  loading,
  mapData,
  currentMarker,
  onPressMarker,
  centerPoint,
  zoomLevel,
  isIssuePointDetailsVisible,
  setIsIssuePointDetailsVisible,
}: ViewProps) => {
  const [mapIsLoaded, setMapIsLoaded] = React.useState(false);
  const mapInstance = React.useRef<MapType>();
  const handleMapInstanceChange = React.useCallback((instance: any) => {
    if (!instance || mapInstance.current != null) return;
    mapInstance.current = instance as MapType;
    mapInstance.current.events.add('mousedowndefaultaction', (_e: any) => {
      setIsIssuePointDetailsVisible(false);
    });
  }, []);

  const theme = useTheme();

  const styles = useStyles(({mediaOnly, isDesktop}) => ({
    root: {
      maxWidth: '100%',
      width: '100%',
      position: 'relative',
    },
    info: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1,
      backgroundColor: theme.color.backgroundMenuColor,
      paddingBottom: 12,
      paddingHorizontal: 16,
      overflowY: 'auto',
      ...mediaOnly(DeviceSize.tablet, {maxHeight: 260}),
      ...mediaOnly(DeviceSize.mobile, {maxHeight: 220}),
    },
    infoRow: {
      marginBottom: 8,
    },
    rowWithIcon: {
      flexDirection: 'row',
      paddingRight: 32,
    },
    icon: {
      marginRight: 12,
      color: theme.color.primaryColor,
      justifyContent: 'center',
      height: 16,
    },
    mapBox: {
      flex: 1,
      width: '100%',
    },
    mapView: {
      ...mediaOnly(DeviceSize.tablet, {height: 426}),
      ...mediaOnly(DeviceSize.mobile, {height: 360}),
    },
    groupItem: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: 12,
      paddingVertical: 5,
    },
    btn: {
      marginBottom: isDesktop ? 40 : 10,
      width: isDesktop ? '100%' : 260,
      textAlign: 'center',
      padding: 6,
    },
  }));

  const SelectField = withStyles({
    root: {
      '& label.Mui-focused': {
        color: theme.color.primaryColor,
      },
      '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
          borderColor: theme.color.primaryColor,
        },
      },
      '& .MuiSelect-select': {
        fontSize: 14,
      },
    },
  })(TextField);

  return (
    <View style={styles.root}>
      <SelectField
        id='issuepoint-select'
        label='Выбрать пункт выдачи'
        variant='outlined'
        select
        size='small'
        margin='normal'
        SelectProps={{fullWidth: true, autoWidth: true}}
        value={currentMarker?.id}>
        {mapData.map((issuePoint) => (
          <MenuItem key={issuePoint.id} onClick={() => onPressMarker(issuePoint)} value={issuePoint.id}>
            {issuePoint.address || issuePoint.name}
          </MenuItem>
        ))}
      </SelectField>
      <View style={styles.mapBox}>
        <LoadableComponent loading={!!loading}>
          {currentMarker && isIssuePointDetailsVisible ? (
            <View style={styles.info}>
              <Text style={styles.infoRow} type='bodySmall'>
                Отделение {currentMarker?.name || currentMarker?.organization.name}
              </Text>
              <View style={[styles.infoRow, styles.rowWithIcon]}>
                <View style={styles.icon}>
                  <images.Location width={18} height={18} color={theme.color.primaryColor} />
                </View>
                <Text type='caption'>{currentMarker?.address}</Text>
              </View>
              <View style={[styles.infoRow, styles.rowWithIcon]}>
                <Link
                  testID='phone-geo-map-link-id'
                  href={`tel:${formatPhoneToCallable(currentMarker?.phone)}`}
                  external
                  style={styles.groupItem}>
                  <View style={styles.icon}>
                    <images.Phone width={18} height={18} color={theme.color.primaryColor} />
                  </View>
                  <Text type='caption'>{formatPhone(currentMarker?.phone)}</Text>
                </Link>
              </View>
              <View style={[styles.infoRow, styles.rowWithIcon]}>
                <View style={styles.icon}>
                  <images.Cards width={18} height={18} color={theme.color.primaryColor} />
                </View>
                <Text type='caption'>Карт в наличии {currentMarker?.availableCardsCount}</Text>
              </View>
              <Text style={styles.infoRow} type='bodySmall'>
                Часы работы
              </Text>
              <View style={styles.rowWithIcon}>
                <View style={styles.icon}>
                  <images.Clock2 width={18} height={18} color={theme.color.primaryColor} />
                </View>
                <Text type='caption'>{currentMarker?.operatingMode}</Text>
              </View>
            </View>
          ) : null}
          <View style={styles.mapView}>
            <YMaps>
              <Map
                instanceRef={handleMapInstanceChange}
                options={{autoFitToViewport: 'always'}}
                height='100%'
                width='100%'
                state={{center: centerPoint, zoom: zoomLevel || initialYandexZoomValue}}
                defaultState={{center: centerPoint, zoom: (zoomLevel || initialYandexZoomValue) - 0.8}}
                onLoad={() => setMapIsLoaded(true)}>
                {mapIsLoaded && (
                  <Clusterer options={{preset: 'islands#invertedOrangeClusterIcons'}}>
                    {mapData.map((marker) => (
                      <Placemark
                        key={marker.id}
                        geometry={marker.geolocation ?? undefined}
                        onClick={() => onPressMarker(marker)}
                        options={{
                          iconLayout: 'default#image',
                          iconImageHref: marker === currentMarker ? MapMarker : MapPin,
                          iconImageSize: [39, 39],
                          iconImageOffset: [-30, -60],
                        }}
                      />
                    ))}
                  </Clusterer>
                )}
              </Map>
            </YMaps>
          </View>
        </LoadableComponent>
      </View>
    </View>
  );
};
